import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LifeBuoy, Star } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { FEEDBACK_STATUSES, type Feedback, apiError } from './lib';

interface StaffMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

type Flow = 'recovery' | 'note' | 'resolve' | 'dismiss';

/**
 * §23 / P12-06 — service recovery is a *status on the feedback row*, not a
 * second entity: acknowledge → assign an owner → timestamped notes → resolve.
 */
export default function FeedbackTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canUpdate = can('customers.update');

  const [rows, setRows] = useState<Feedback[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [recoveryOnly, setRecoveryOnly] = useState(false);

  const [flow, setFlow] = useState<{ kind: Flow; entry: Feedback } | null>(null);
  const [assigneeId, setAssigneeId] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Feedback[] }>('/feedback', {
        params: {
          limit: 100,
          status: status || undefined,
          recoveryOnly: recoveryOnly ? true : undefined,
        },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('crm.feedback.loadFailed'))))
      .finally(() => setLoading(false));
  }, [status, recoveryOnly, t]);

  useEffect(load, [load]);

  useEffect(() => {
    // `users.view` gates the staff list; without it the owner defaults to the
    // caller, which is exactly what the API does when `assigneeId` is omitted.
    api
      .get<{ data: StaffMember[] }>('/users', { params: { limit: 100 } })
      .then(({ data }) => setStaff(data.data ?? []))
      .catch(() => setStaff([]));
  }, []);

  const acknowledge = async (entry: Feedback) => {
    try {
      await api.post(`/feedback/${entry.id}/acknowledge`, {});
      toast.success(t('crm.feedback.acknowledged'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('crm.feedback.failed')));
    }
  };

  const runFlow = async () => {
    if (!flow) return;
    const { kind, entry } = flow;
    try {
      if (kind === 'recovery') {
        await api.post(`/feedback/${entry.id}/recovery`, {
          assigneeId: assigneeId || undefined,
          note: note || undefined,
        });
      } else if (kind === 'note') {
        await api.post(`/feedback/${entry.id}/notes`, { resolutionNote: note });
      } else if (kind === 'resolve') {
        await api.post(`/feedback/${entry.id}/resolve`, { resolutionNote: note || undefined });
      } else {
        await api.post(`/feedback/${entry.id}/dismiss`, { resolutionNote: note || undefined });
      }
      toast.success(t(`crm.feedback.${kind}Done`));
      setFlow(null);
      setNote('');
      setAssigneeId('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('crm.feedback.failed')));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          className="w-48"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('crm.feedback.status')}
        >
          <option value="">{t('crm.feedback.allStatuses')}</option>
          {FEEDBACK_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`crm.feedbackStatus.${value}`)}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={recoveryOnly}
            onChange={(e) => setRecoveryOnly(e.target.checked)}
          />
          {t('crm.feedback.recoveryOnly')}
        </label>
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('crm.feedback.empty')} hint={t('crm.feedback.emptyHint')} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((entry) => (
            <Card key={entry.id}>
              <CardHeader className="flex-row items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    {t('crm.feedback.ratingOf', { rating: entry.rating })}
                  </CardTitle>
                  <p className="text-xs text-ink-muted">
                    {entry.customer?.name ?? t('crm.feedback.anonymous')} ·{' '}
                    {formatDateTime(entry.createdAt, i18n.language)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge tone={toneForStatus(entry.status)}>
                    {t(`crm.feedbackStatus.${entry.status}`)}
                  </Badge>
                  {entry.isServiceRecovery && (
                    <Badge tone="danger">{t('crm.feedback.recoveryCase')}</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {entry.comment && <p className="text-sm">{entry.comment}</p>}

                <div className="flex flex-wrap gap-2 text-xs text-ink-muted">
                  {entry.foodRating != null && (
                    <span>{t('crm.feedback.food', { rating: entry.foodRating })}</span>
                  )}
                  {entry.serviceRating != null && (
                    <span>{t('crm.feedback.service', { rating: entry.serviceRating })}</span>
                  )}
                  {entry.speedRating != null && (
                    <span>{t('crm.feedback.speed', { rating: entry.speedRating })}</span>
                  )}
                </div>

                {entry.assignee && (
                  <p className="flex items-center gap-1.5 text-xs">
                    <LifeBuoy className="h-3.5 w-3.5 text-accent" />
                    {t('crm.feedback.ownedBy', {
                      name: `${entry.assignee.firstName ?? ''} ${entry.assignee.lastName ?? ''}`.trim(),
                    })}
                  </p>
                )}

                {entry.resolutionNote && (
                  <pre className="scrollbar-thin max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-subtle p-3 text-xs">
                    {entry.resolutionNote}
                  </pre>
                )}

                {canUpdate && (
                  <div className="flex flex-wrap gap-2">
                    {!entry.acknowledgedAt && (
                      <Button size="sm" variant="outline" onClick={() => acknowledge(entry)}>
                        {t('crm.feedback.acknowledge')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFlow({ kind: 'recovery', entry })}
                    >
                      {t('crm.feedback.assignOwner')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFlow({ kind: 'note', entry })}
                    >
                      {t('crm.feedback.addNote')}
                    </Button>
                    <Button size="sm" onClick={() => setFlow({ kind: 'resolve', entry })}>
                      {t('crm.feedback.resolve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setFlow({ kind: 'dismiss', entry })}
                    >
                      {t('crm.feedback.dismiss')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {flow && (
        <Dialog
          open
          onClose={() => setFlow(null)}
          title={t(`crm.feedback.${flow.kind}Title`)}
          description={t(`crm.feedback.${flow.kind}Hint`)}
          footer={
            <>
              <Button variant="outline" onClick={() => setFlow(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={runFlow}
                disabled={flow.kind === 'note' && !note.trim()}
              >
                {t('common.confirm')}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {flow.kind === 'recovery' && (
              <Field label={t('crm.feedback.owner')} hint={t('crm.feedback.ownerHint')}>
                <Select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                  <option value="">{t('crm.feedback.assignToMe')}</option>
                  {staff.map((member) => (
                    <option key={member.id} value={member.id}>
                      {`${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.id}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={t('crm.feedback.note')} required={flow.kind === 'note'}>
              <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}
